import NetworkCall from "network/networkCall";
import Request from "network/request";
import K from "utilities/constants";
import {
  setSelectedClient,
  editClientDataInfo,
} from "redux/slices/clientSlice";
import {
  camelCaseKeys,
  camelCaseKeysRecursively,
} from "utilities/generalUtility";
import { addClient } from "redux/slices/userSlice";

export default class Client {
  static getById(clientId) {
    return async (dispatch, getState) => {
      const { selectedClient } = getState().clients;
      if (selectedClient?.id !== clientId) {
        const res = await NetworkCall.fetch(Request.getClientById(clientId));
        dispatch(setSelectedClient(camelCaseKeysRecursively(res.data)));
      }
    };
  }

  static editClientInformation(body) {
    return async (dispatch) => {
      const res = await NetworkCall.fetch(Request.editClientInformation(body));
      dispatch(editClientDataInfo(camelCaseKeysRecursively(res.data)));
    };
  }

  static create(data) {
    return async (dispatch) => {
      const res = await NetworkCall.fetch(Request.createClient(data));
      dispatch(addClient(camelCaseKeysRecursively(res.data)));
    };
  }

  static async getJobBillingRules(clientId) {
    const res = await NetworkCall.fetch(
      Request.getJobBillingRulesById(clientId),
    );
    return camelCaseKeysRecursively(res.data);
  }

  static async getClientBillingRules(clientId) {
    const res = await NetworkCall.fetch(
      Request.getClientBillingRulesById(clientId),
    );
    return camelCaseKeysRecursively(res.data);
  }

  static async addClientLevelBillingRules(body) {
    const res = await NetworkCall.fetch(
      Request.addClientLevelBillingRules(body),
      false,
    );
    return res;
  }

  static async addJobLevelBillingRules(body) {
    const res = await NetworkCall.fetch(
      Request.addJobLevelBillingRules(body),
      false,
    );
    return camelCaseKeysRecursively(res.data);
  }

  static async updateTierDetails(body) {
    const res = await NetworkCall.fetch(Request.updateTierDetails(body), false);
    return camelCaseKeysRecursively(res.data);
  }

  static async addTier(values) {
    const res = await NetworkCall.fetch(Request.addTier(values));
    return res;
  }

  static async addClientJobs(values) {
    const res = await NetworkCall.fetch(
      Request.addClientJobsById(values),
      false,
    );
    return res.data;
  }

  static async editClientJobs(values) {
    const res = await NetworkCall.fetch(
      Request.editClientJobs({ ...values }),
      false,
    );
    return res.data;
  }

  static async getTiersById(clientId, loading = true) {
    const res = await NetworkCall.fetch(
      Request.getClientTiersById(clientId),
      loading,
    );
    return res.data.map((obj) => camelCaseKeys(obj));
  }

  static async getAllClientName() {
    const res = await NetworkCall.fetch(Request.getAllClientName());
    return res.data.map((obj) => camelCaseKeys(obj));
  }

  static async abortClientBillingRule(clientBillingRuleId) {
    const res = await NetworkCall.fetch(
      Request.abortClientBillingRule(clientBillingRuleId),
    );
    return camelCaseKeysRecursively(res);
  }

  static async abortJobBillingRule(jobBillingRuleId) {
    const res = await NetworkCall.fetch(
      Request.abortJobBillingRule(jobBillingRuleId),
    );
    return camelCaseKeysRecursively(res);
  }

  static async editUpcomingClientBillingRule(values) {
    const res = await NetworkCall.fetch(
      Request.editUpcomingClientBillingRule(values),
      false,
    );
    return res;
  }

  static async editUpcomingJobBillingRule(values) {
    const res = await NetworkCall.fetch(
      Request.editUpcomingJobBillingRule(values),
      false,
    );
    return res;
  }

  static async editBillingRuleStartDate(payload, loader = true) {
    await NetworkCall.fetch(
      Request.editClientBillingRuleStartDate(payload),
      loader,
    );
  }

  static async editExistigJobLevelBillingRuleDate(body) {
    const res = await NetworkCall.fetch(
      Request.editExistingJobLevelBillingRuleDate(body),
      false,
    );
    return camelCaseKeysRecursively(res.data);
  }

  static async createAttachment(body) {
    const res = await NetworkCall.fetch(Request.createAttachment(body), false);
    return camelCaseKeysRecursively(res.data);
  }

  static async getAttachment(
    body,
    { showError = true, skipForbiddenRedirect = false } = {},
  ) {
    const res = await NetworkCall.fetch(
      Request.getAttachment(body),
      true,
      showError,
      skipForbiddenRedirect,
    );
    return camelCaseKeysRecursively(res.data);
  }

  static async createCvAttachment(entryId, file) {
    const { filepath } = await Client.uploadAttachement(file, K.S3Folders.CV);
    return Client.createAttachment({
      entityId: entryId,
      type: K.AttachmentType.Candidate,
      title: "Resume/CV",
      attachment: filepath,
    });
  }

  static async uploadAttachement(file, folder) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folderName", folder);
    const res = await NetworkCall.fetch(Request.uploadFile(formData), false);

    return camelCaseKeysRecursively(res.data);
  }

  static async getPreSignedUrl(url) {
    const res = await NetworkCall.fetch(Request.getPreSignedUrl(url), false);
    return camelCaseKeysRecursively(res);
  }

  static async deleteFileFromS3(url) {
    const res = await NetworkCall.fetch(Request.deleteFileFromS3(url), false);
    return camelCaseKeysRecursively(res);
  }

  static async deleteAttachement(id) {
    const res = await NetworkCall.fetch(Request.deleteAttachment(id), false);
    return camelCaseKeysRecursively(res);
  }

  static async addClientClass(body) {
    const res = await NetworkCall.fetch(Request.addClientClass(body), false);
    return camelCaseKeysRecursively(res);
  }

  static async editClientClass(classId, body) {
    const res = await NetworkCall.fetch(Request.editClientClass(classId, body), false);
    return camelCaseKeysRecursively(res);
  }

  static async getClientClassList(body, useLoading = true) {
    const res = await NetworkCall.fetch(
      Request.getClientClassList(body),
      useLoading,
    );
    return camelCaseKeysRecursively(res.data);
  }

  static async getAllRecruiterForExcelUpload(body) {
    const res = await NetworkCall.fetch(
      Request.getAllRecruitersForExcelUpload(body),
    );
    return camelCaseKeysRecursively(res);
  }

  static async getAllClassForExcelUpload(body) {
    const res = await NetworkCall.fetch(
      Request.getAllClassForExcelUpload(body),
    );
    return camelCaseKeysRecursively(res);
  }

  static v2 = {
    get: async (params, useLoading = true) => {
      return NetworkCall.fetch(Request.v2.getClients(params), useLoading);
    },

    getRecruitersByClientId: (
      clientId,
      params,
      useLoading = true,
      showError = true,
    ) => {
      return NetworkCall.fetch(
        Request.v2.getClientRecruiters(clientId, params),
        useLoading,
        showError,
      );
    },
  };
}
